/**
 * The soundtrack's cues and their captured register streams (spec 07 §2-3). Pure: shared by the
 * browser player and the offline render tool.
 */

import { unpackFrames } from '../sid/capture';
import type { SidTune } from '../sid/sidTimeline';

/** Subtune cues in `content/music/c64.json`, in subtune order (#1 .. #12). */
export const MUSIC_CUES = [
  'introAndMain', 'main', 'bumblebee', 'bonus', 'gameOver', 'mainHurry',
  'extend', 'title', 'hurryUp', 'skelMonsta', 'secretRoom', 'badEnding',
] as const;

/** A music cue: one subtune of the C64 soundtrack. */
export type MusicCue = (typeof MUSIC_CUES)[number];

/** One subtune entry of `content/music/c64.json`. */
export interface CapturedSubtune {
  n: number;
  cue: string;
  loopFrame: number;
  /** Base64 of the packed init writes (one frame). */
  init: string;
  /** Base64 of the packed frames. */
  data: string;
}

/**
 * The in-game tunes: the only ones under which the C64 starts a sound effect (D_F1AC $F1E4: song
 * offset below $19 or equal to $27 - subtunes 1, 2, 3 and 6).
 */
export const EFFECT_CUES: ReadonlySet<MusicCue> = new Set<MusicCue>(['introAndMain', 'main', 'bumblebee', 'mainHurry']);

export function isMusicCue(s: string): s is MusicCue {
  return (MUSIC_CUES as readonly string[]).includes(s);
}

/** Bytes of a base64 string (the captures' packed frames). */
export function base64Bytes(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Unpack a subtune's base64 register stream. */
export function decodeTune(s: CapturedSubtune): SidTune {
  return {
    init: unpackFrames(base64Bytes(s.init))[0] ?? [],
    frames: unpackFrames(base64Bytes(s.data)),
    loopFrame: s.loopFrame,
  };
}
